import React, { useState } from "react";
import ChannelIcon from "./ChannelIcon";
import Tooltip from "./Tooltip";
import InfoIcon from "./InfoIcon";

const ChannelSentimentTable = ({ data }) => {
  const [sortConfig, setSortConfig] = useState({
    key: 'positive',
    direction: 'desc'
  });

  const sortedData = [...data].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) {
      return null;
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const renderFrequentWords = (words) => {
    if (!words || words.length === 0) return <span className="text-grey-60">No data</span>;
    
    return (
      <div className="flex flex-wrap gap-1">
        {words.slice(0, 5).map((word, index) => {
          let bgColor = 'bg-grey-20';
          if (word.sentiment === 'positive') bgColor = 'bg-[#DAFBEB]';
          if (word.sentiment === 'negative') bgColor = 'bg-[#FBDADA]';
          
          return (
            <span key={index} className={`px-2 py-0.5 rounded-md text-sm ${bgColor}`}>
              {word.text}
            </span>
          );
        })}
      </div>
    );
  };

  const renderSentimentChips = (channel) => (
    <div className="flex items-center gap-2">
      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-sm bg-[#DAFBEB] text-black-0">
        {channel.positive}%
      </span>
      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-sm bg-grey-20 text-black-0">
        {channel.neutral}%
      </span>
      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-sm bg-[#FBDADA] text-black-0">
        {channel.negative}%
      </span>
    </div>
  );

  return (
    <div>
      <h3 className="text-md font-normal text-black-0 px-10 py-3">Channel sentiment</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-grey-20">
          <thead>
            <tr>
              <th 
                onClick={() => requestSort('channel')}
                className="pl-10 pr-5 py-2 text-left text-sm font-normal text-grey-60 cursor-pointer hover:text-black-0"
              >
                Channel {getSortDirectionIndicator('channel')}
              </th>
              <th 
                onClick={() => requestSort('positive')}
                className="pl-10 pr-5 py-2 text-left text-sm font-normal text-grey-60 cursor-pointer hover:text-black-0"
              >
                <span className="inline-flex items-center gap-1">
                  Sentiment {getSortDirectionIndicator('positive')}
                  <Tooltip content="Positive / neutral / negative share for the channel." placement="bottom" align="start">
                    <span className="inline-flex cursor-help">
                      <InfoIcon className="w-4 h-4" />
                    </span>
                  </Tooltip>
                </span>
              </th>
              <th 
                className="pl-10 pr-5 py-2 text-left text-sm font-normal text-grey-60"
              >
                <span className="inline-flex items-center gap-1">
                  Frequent words
                  <Tooltip content="Most frequent words, labeled by sentiment where available." placement="bottom" align="start">
                    <span className="inline-flex cursor-help">
                      <InfoIcon className="w-4 h-4" />
                    </span>
                  </Tooltip>
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-grey-20">
            {sortedData.map((channel) => (
              <tr key={channel.id} className="hover:bg-white-f">
                <td className="pl-10 pr-5 py-3 text-sm font-medium text-black-0">
                  <div className="flex items-center gap-1">
                    <ChannelIcon isPrivate={channel.is_private} size={14} />
                    <span>{channel.channel}</span>
                  </div>
                </td>
                <td className="pl-10 pr-5 py-3 text-sm font-normal">
                  {renderSentimentChips(channel)}
                </td>
                <td className="pl-10 pr-5 py-3 text-sm">
                  {renderFrequentWords(channel.frequentWords)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ChannelSentimentTable;
